import { fetchJson } from "./client";
import type {
  PortsResponse,
  ScheduleResponse,
  ScheduleVoyage,
  Voyage,
  VoyageDetails,
} from "./types";

// /api/ports — defaults to the 20 transit waypoints; pass limit=10000 for the
// full operational port list.
export function getPorts(limit = 10000): Promise<PortsResponse> {
  return fetchJson<PortsResponse>(`/api/ports?limit=${limit}`);
}

// /api/ports/schedule — voyages per terminal for a given month.
// portCodes is repeated as a query parameter, one entry per terminal
// (the externalRef like "BEANR8", not the long portName). The backend
// translates each code to the corresponding portName before forwarding
// upstream and before computing the cache key, so URLs stay short for
// the frontend while cache + upstream remain on their legacy shape.
export async function getSchedule(params: {
  portCodes: string[];
  year: number;
  month: number;            // 1..12
  oprTypeFilter?: string;   // e.g. "TCOV,OVOV"
}): Promise<ScheduleResponse> {
  const q = new URLSearchParams();
  for (const code of params.portCodes) q.append("portCodes", code);
  q.set("year", String(params.year));
  q.set("month", String(params.month));
  if (params.oprTypeFilter) q.set("oprTypeFilter", params.oprTypeFilter);
  type RawVoyage = Omit<ScheduleVoyage, "voyageNo"> & { voyageNo: number | string };
  const res = await fetchJson<{ voyages: RawVoyage[] }>(`/api/ports/schedule?${q.toString()}`);
  // voyageNo arrives as number OR string depending on operator —
  // normalise once at the boundary so === is safe everywhere else.
  return { voyages: (res.voyages ?? []).map((v) => ({ ...v, voyageNo: String(v.voyageNo) })) };
}

// /api/voyages — flat list of all voyages (~500 records).
export function getVoyages(): Promise<Voyage[]> {
  return fetchJson<Voyage[]>("/api/voyages");
}

// /api/date-events — single source of truth for both user-created
// events and synced public holidays.
//
// Pass `sync: [{country, year}, …]` to ask the backend to refresh its
// holiday rows for the given (country, year) pairs from
// date.nager.at before returning. Backend caches the upstream call
// TTL-bypassed so repeated syncs are free.
import type { DateEvent, VoyageStopOverride } from "../../../../backend/shared";

export type { VoyageStopOverride } from "../../../../backend/shared";

export function getDateEvents(
  sync?: ReadonlyArray<{ country: string; year: number }>,
): Promise<DateEvent[]> {
  let url = "/api/date-events";
  if (sync && sync.length > 0) {
    const pairs = sync
      .filter((s) => /^[A-Z]{2}$/.test(s.country) && Number.isFinite(s.year))
      .map((s) => `${s.country}-${s.year}`)
      .join(",");
    if (pairs) url += `?sync=${pairs}`;
  }
  return fetchJson<DateEvent[]>(url);
}

export async function putDateEvents(events: Omit<DateEvent, "id">[]): Promise<DateEvent[]> {
  const res = await fetch("/api/date-events", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(events),
  });
  if (!res.ok) throw new Error(`putDateEvents: HTTP ${res.status}`);
  return (await res.json()) as DateEvent[];
}

// /api/voyage-details/{vesselCode}/{voyageNo}
export function getVoyageDetails(
  vesselCode: string,
  voyageNo: string,
): Promise<VoyageDetails> {
  return fetchJson<VoyageDetails>(
    `/api/voyage-details/${encodeURIComponent(vesselCode)}/${encodeURIComponent(voyageNo)}`,
  );
}

// /api/voyage-overrides — per-stop user overrides for a voyage
// (stevedore working window + L/D/Shifting volume overrides). Whole-
// voyage replace on PUT; backend drops rows where every override
// field is null.
export function getVoyageOverrides(
  vesselCode: string,
  voyageNo: string,
): Promise<VoyageStopOverride[]> {
  const q = new URLSearchParams({ vesselCode, voyageNo });
  return fetchJson<VoyageStopOverride[]>(`/api/voyage-overrides?${q.toString()}`);
}

// Bulk-fetch every override row in the DB. Used by the calendar's
// viewport-mount prewarm so stripes render in one round-trip.
export function getAllVoyageOverrides(): Promise<VoyageStopOverride[]> {
  return fetchJson<VoyageStopOverride[]>("/api/voyage-overrides?all=1");
}

export async function putVoyageOverrides(
  vesselCode: string,
  voyageNo: string,
  rows: VoyageStopOverride[],
): Promise<VoyageStopOverride[]> {
  const q = new URLSearchParams({ vesselCode, voyageNo });
  const res = await fetch(`/api/voyage-overrides?${q.toString()}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(rows),
  });
  if (!res.ok) throw new Error(`putVoyageOverrides: HTTP ${res.status}`);
  return (await res.json()) as VoyageStopOverride[];
}

// /api/view-templates — saved Port Calendar lane configurations
// (terminal externalRef list + ordering). Shared across all users.
// Type owned by backend/shared.ts (single source of truth, safe to
// pull from both backend stores and the frontend).
export type { ViewTemplate } from "../../../../backend/shared";
import type { ViewTemplate } from "../../../../backend/shared";

export function getViewTemplates(): Promise<ViewTemplate[]> {
  return fetchJson<ViewTemplate[]>("/api/view-templates");
}

// Returns the created template on success, or null on a name collision.
// Other errors surface as throws so the caller can show a generic toast.
export async function postViewTemplate(name: string, ports: string[]): Promise<ViewTemplate | { error: "name-taken" }> {
  const res = await fetch("/api/view-templates", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name, ports }),
  });
  if (res.status === 409) return { error: "name-taken" };
  if (!res.ok) throw new Error(`postViewTemplate: HTTP ${res.status}`);
  return (await res.json()) as ViewTemplate;
}

export async function putViewTemplate(id: number, name: string, ports: string[]): Promise<ViewTemplate | { error: "name-taken" | "not-found" }> {
  const res = await fetch(`/api/view-templates/${id}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name, ports }),
  });
  if (res.status === 409) return { error: "name-taken" };
  if (res.status === 404) return { error: "not-found" };
  if (!res.ok) throw new Error(`putViewTemplate: HTTP ${res.status}`);
  return (await res.json()) as ViewTemplate;
}

export async function deleteViewTemplate(id: number): Promise<void> {
  const res = await fetch(`/api/view-templates/${id}`, { method: "DELETE" });
  if (!res.ok && res.status !== 404) throw new Error(`deleteViewTemplate: HTTP ${res.status}`);
}

// /api/current-view — per-user auto-saved lane state (externalRef codes
// + ordering). Returns empty ports when the user has never saved one.
// Type owned by backend/shared.ts (same rationale as ViewTemplate).
export type { CurrentView } from "../../../../backend/shared";
import type { CurrentView } from "../../../../backend/shared";

export function getCurrentView(): Promise<CurrentView> {
  return fetchJson<CurrentView>("/api/current-view");
}

export async function putCurrentView(ports: string[]): Promise<CurrentView> {
  const res = await fetch("/api/current-view", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ ports }),
  });
  if (!res.ok) throw new Error(`putCurrentView: HTTP ${res.status}`);
  return (await res.json()) as CurrentView;
}

// /api/cache — operator-triggered upstream cache management. GET
// returns current entry count; DELETE wipes everything so subsequent
// requests go back to upstream.
export interface CacheState { count: number }
export interface CacheClearResult { cleared: number }

export function getCacheState(): Promise<CacheState> {
  return fetchJson<CacheState>("/api/cache");
}
export async function clearUpstreamCache(): Promise<CacheClearResult> {
  const res = await fetch("/api/cache", { method: "DELETE" });
  if (!res.ok) throw new Error(`clearUpstreamCache: HTTP ${res.status}`);
  return (await res.json()) as CacheClearResult;
}
